import React, { useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { Routes, Route, useLocation } from 'react-router'
import { isUndefined, isNil, isEmpty, get } from 'lodash'
import pluralize from 'pluralize'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise'
import ddRum from '../lib/datadogRum'

import 'normalize.css'
import 'purecss/build/pure.css'
import 'purecss/build/grids.css'
import 'purecss/build/grids-responsive.css'

import Login from './Login'
import Auth0AutoRedirect from './Auth0/Auth0AutoRedirect'
import Menu from './common/Menu'
import MenuButton from './common/MenuButton'
import EntitledRoutes from './EntitledRoutes'
import PasswordReset from './PasswordReset'
import ForgotPassword from './ForgotPassword'
import FlashMessage from './common/FlashMessage'
import { getCurrentSession, clearSession } from '../actions/Login'
import GlobalLoadingIndicator from './common/GlobalLoadingIndicator'
import { selectIsGloballyLoading } from '../selectors/GlobalLoading'
import { menuSections } from './RouteDefinitions'
import RedirectTo from './RedirectTo'
import logo from '../assets/logo.png'

import '../css/Howie.scss'

pluralize.addPluralRule('embargo', 'embargoes')

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule])

LicenseManager.setLicenseKey(
  'Evaluation_License_Valid_Until__25_August_2018__MTUzNTE1MTYwMDAwMA==53aedc27a9df43c4e4bd6c97d9e6f413'
)

const Howie = ({
  needsSession,
  needsSessionUser,
  getCurrentSession: getCurrentSessionAction,
  clearSession: clearSessionAction,
  entitlements,
  flashMessages,
  isGloballyLoading,
  userRoles,
  user,
  auth0SsoEnabled,
  auth0Domain
}) => {
  const location = useLocation()

  useEffect(() => {
    if (needsSessionUser && !needsSession) {
      getCurrentSessionAction()
    }
  }, [needsSessionUser, needsSession, getCurrentSessionAction])

  useEffect(() => {
    // Track route changes with Datadog RUM
    ddRum.startView({ name: location.pathname })
  }, [location])

  useEffect(() => {
    // Identify user in Datadog RUM when logged in
    if (user && user.id) {
      ddRum.setUser({
        id: user.id.toString(),
        email: user.email,
        name: user.username,
        user_type: user.user_type,
        roles: user.roles ? user.roles.join(', ') : ''
      })
    }
  }, [user])

  const handleLogout = () => {
    ddRum.clearUser()
    clearSessionAction()
  }

  const userEntitledTo = useCallback((group, action) => {
    return get(entitlements, `${group}.${action}.access`, false)
  }, [entitlements])

  const userHasAnyRole = useCallback((roles) => {
    return roles.some((role) => userRoles?.includes(role))
  }, [userRoles])

  const userRoleIsAllowed = useCallback((allowedRoles) => {
    if (isEmpty(allowedRoles)) {
      return true
    }

    return userHasAnyRole(allowedRoles)
  }, [userHasAnyRole])

  const isCVTUser = useMemo(() => {
    return userRoles?.length === 1 && userRoles[0] === 'carrier_qa'
  }, [userRoles])

  const entitledSectionRoutes = useMemo(() => {
    if (isNil(entitlements)) {
      return []
    }

    return menuSections.reduce((memo, sectionRoute) => {
      const routes = sectionRoute.routes.filter(({ group, action, allowedRoles }) => {
        return userEntitledTo(group, action) && userRoleIsAllowed(allowedRoles)
      })

      if (!isEmpty(routes)) {
        memo.push({
          title: sectionRoute.title,
          routes: routes.map(({ title, path, label }) => ({ title, path, label }))
        })
      }

      return memo
    }, [])
  }, [entitlements, userEntitledTo, userRoleIsAllowed])

  const filteredSectionRoutes = useMemo(() => {
    if (!isCVTUser) { return entitledSectionRoutes }

    return entitledSectionRoutes.filter((route) => route.title !== 'Platforms')
  }, [isCVTUser, entitledSectionRoutes])

  const logoutButton = () => {
    return <MenuButton title="Logout" handleClick={handleLogout} />
  }

  const login = () => {
    // Flag on: skip Howie's login page and auto-redirect to Auth0 Universal
    // Login. Mirror Auth0Bootstrap's provider-mount gate (auth0SsoEnabled &&
    // auth0Domain) exactly — Auth0AutoRedirect calls useAuth0(), which only
    // works inside the Auth0Provider that Bootstrap mounts under that same
    // condition. A flag-on-but-no-domain config falls back to legacy login. PD-27768.
    if (auth0SsoEnabled && auth0Domain) {
      return <Auth0AutoRedirect />
    }

    return (
      <>
        <RedirectTo />
        <div className="MainContainer">
          <Routes>
            <Route key="passwordReset" path="password_reset" element={<PasswordReset />} />
            <Route key="forgotPassword" path="forgot_password" element={<ForgotPassword />} />
            <Route key="login" path="*" element={<Login />} />
          </Routes>
        </div>
      </>
    )
  }

  const router = () => {
    return (
      <>
        <RedirectTo />
        <GlobalLoadingIndicator isLoading={isGloballyLoading} />
        <div>
          <header className="main">
            <div className="logo">
              <a href="/">
                <h1>Howie <small>by</small> <img src={logo} alt="Logo" /></h1>
              </a>
            </div>
            <Menu sectionRoutes={filteredSectionRoutes} />
            <div className="actions">
              {logoutButton()}
            </div>
          </header>
          <div className="general-flash-message">
            <FlashMessage messages={flashMessages} tag="general" />
          </div>
          <EntitledRoutes entitlements={entitlements} />
        </div>
      </>
    )
  }

  if (needsSession) {
    return login()
  }

  if (needsSessionUser) {
    return null
  }

  if (isNil(entitlements)) {
    return null
  }

  return router()
}

Howie.propTypes = {
  needsSession: PropTypes.bool.isRequired,
  needsSessionUser: PropTypes.bool.isRequired,
  getCurrentSession: PropTypes.func.isRequired,
  clearSession: PropTypes.func.isRequired,
  entitlements: PropTypes.object,
  flashMessages: PropTypes.array.isRequired,
  isGloballyLoading: PropTypes.bool.isRequired,
  userRoles: PropTypes.array,
  user: PropTypes.object,
  auth0SsoEnabled: PropTypes.bool,
  auth0Domain: PropTypes.string
}

const mapStateToProps = (state) => ({
  needsSession: !isUndefined(state.login.session.generalError) || isUndefined(state.login.session.nonce),
  needsSessionUser: isUndefined(state.login.session.user),
  userType: get(state, 'login.session.user.user_type'),
  userRoles: get(state, 'login.session.user.roles'),
  sessionError: get(state, 'login.session.generalError'),
  entitlements: get(state, 'login.session.configuration.entitlements'),
  flashMessages: state.flashMessages.messages,
  isGloballyLoading: selectIsGloballyLoading(state),
  user: get(state, 'login.session.user'),
  auth0SsoEnabled: get(state, 'login.oauthConfig.auth0SsoEnabled'),
  auth0Domain: get(state, 'login.oauthConfig.auth0Domain')
})

const mapDispatchToProps = (dispatch) => bindActionCreators({ getCurrentSession, clearSession }, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(Howie)
